import CtaIcon from './ui/CtaIcon';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import React, { useState } from 'react';
import type { AssessmentAdminStatus, CollectibilityData } from '../types/collectibility';

export type { CollectibilityData };

interface CollectibilityPanelProps {
  data: CollectibilityData;
  compact?: boolean;
  defaultExpanded?: boolean;
  showAdminActions?: boolean;
  onRerunAssessment?: () => Promise<void>;
  onCancelAssessment?: () => Promise<void>;
  onResetPipeline?: () => Promise<void>;
  onResetAndRun?: () => Promise<void>;
  rerunLoading?: boolean;
  cancelLoading?: boolean;
  resetLoading?: boolean;
  resetAndRunLoading?: boolean;
  adminStatus?: AssessmentAdminStatus | null;
  adminPollMeta?: {
    count: number;
    lastAt: string | null;
    lastError: string | null;
  };
}

const formatDuration = (seconds: number): string => {
  if (seconds < 60) return `${seconds}s`;
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
};

const AdminAssessmentStatusPanel: React.FC<{
  adminStatus: AssessmentAdminStatus;
  adminPollMeta?: CollectibilityPanelProps['adminPollMeta'];
  onCancelAssessment?: () => Promise<void>;
  onResetPipeline?: () => Promise<void>;
  onResetAndRun?: () => Promise<void>;
  cancelLoading?: boolean;
  resetLoading?: boolean;
  resetAndRunLoading?: boolean;
}> = ({
  adminStatus,
  adminPollMeta,
  onCancelAssessment,
  onResetPipeline,
  onResetAndRun,
  cancelLoading = false,
  resetLoading = false,
  resetAndRunLoading = false,
}) => (
  <div
    className="mt-4 w-full text-left text-xs bg-[#0a0a0a]/80 border border-orange-500/25 rounded-lg p-3 space-y-1.5 font-mono"
    data-testid="assessment-admin-status"
  >
    <div className="text-orange-300 font-semibold text-[11px] uppercase tracking-wide font-sans">
      Admin · assessment pipeline
    </div>
    <div className="text-gray-300">
      Status: <span className="text-white">{adminStatus.status || 'unknown'}</span>
      {adminStatus.pending_seconds != null && (
        <span className="text-gray-500"> · running {formatDuration(adminStatus.pending_seconds)}</span>
      )}
    </div>
    <div className="text-gray-400">
      Mode: {adminStatus.execution_mode ?? 'auto'}
      {' · '}
      {adminStatus.background_worker_available ? 'background worker available' : 'inline fallback (shared host)'}
      {adminStatus.http_detach_available === false ? ' · no HTTP detach (mod_php)' : ''}
    </div>
    <div className="text-gray-400">
      Prompts: {adminStatus.prompts_available ? 'ok' : 'MISSING'}
      {' · '}
      Worker script: {adminStatus.worker_script_available ? 'ok' : 'MISSING'}
      {' · '}
      AI: {adminStatus.ai_rarity_enabled ? 'enabled' : 'disabled'}
      {adminStatus.ai_provider ? ` · provider: ${adminStatus.ai_provider}` : ''}
      {adminStatus.ai_model ? ` · model: ${adminStatus.ai_model}` : ''}
      {adminStatus.rarity_max_tokens != null ? ` · rarity tokens: ${adminStatus.rarity_max_tokens}` : ''}
      {adminStatus.ai_timeout_seconds != null ? ` · AI timeout: ${adminStatus.ai_timeout_seconds}s` : ''}
      {adminStatus.curl_available === false ? ' · curl: MISSING' : ''}
      {adminStatus.lock_active ? ' · lock: active' : ''}
      {adminStatus.cancel_requested ? ' · cancel: requested' : ''}
    </div>
    {adminStatus.last_recovery_attempt_at && (
      <div className="text-gray-500">
        Last recovery attempt: {new Date(adminStatus.last_recovery_attempt_at).toLocaleString()}
      </div>
    )}
    {adminStatus.bootstrap_ok === false && (
      <div className="text-red-400 font-sans">Bootstrap missing. Deploy assessment_bootstrap.php + BaselineRarityService.php</div>
    )}
    {adminStatus.blockers && adminStatus.blockers.length > 0 && (
      <div className="text-orange-400/90 font-sans space-y-0.5">
        {adminStatus.blockers.map((blocker) => (
          <div key={blocker}><ExclamationTriangleIcon className="inline-block h-4 w-4 align-text-bottom" aria-hidden="true" /> {blocker}</div>
        ))}
      </div>
    )}
    {adminStatus.updated_at && (
      <div className="text-gray-500">Last DB update: {new Date(adminStatus.updated_at).toLocaleString()}</div>
    )}
    {adminStatus.is_stale && (
      <div className="text-orange-400 font-sans">
        {adminStatus.blocking_inline_required
          ? 'Stale pending on mod_php. Click Queue new assessment once (blocking, ~1-2 min). Status polls cannot run workers on this host.'
          : 'Stale pending (>3 min). Recovery runs on vehicle fetch when HTTP detach is available.'}
      </div>
    )}
    {adminStatus.worker_heartbeat?.started_at && (
      <div className="text-gray-500">
        Worker heartbeat: phase {adminStatus.worker_heartbeat.phase ?? 'unknown'}
        {' · '}
        started {new Date(adminStatus.worker_heartbeat.started_at * 1000).toLocaleTimeString()}
      </div>
    )}
    <div className="mt-2 flex flex-wrap gap-2 font-sans">
      {onResetAndRun && (
        <button
          type="button"
          onClick={() => void onResetAndRun()}
          disabled={resetAndRunLoading || resetLoading || cancelLoading}
          className="px-3 py-1.5 rounded-md border border-orange-500/50 text-orange-200 hover:bg-orange-500/10 disabled:opacity-50 text-[11px]"
          data-testid="reset-and-run-assessment-button"
        >
          {resetAndRunLoading ? 'Resetting & running…' : 'Reset & run fresh'}
        </button>
      )}
      {onResetPipeline && (
        <button
          type="button"
          onClick={() => void onResetPipeline()}
          disabled={resetLoading || resetAndRunLoading || cancelLoading}
          className="px-3 py-1.5 rounded-md border border-orange-500/40 text-orange-200 hover:bg-orange-500/10 disabled:opacity-50 text-[11px]"
          data-testid="reset-assessment-pipeline-button"
        >
          {resetLoading ? 'Resetting…' : 'Reset & clear logs'}
        </button>
      )}
      {onCancelAssessment && (
        <button
          type="button"
          onClick={() => void onCancelAssessment()}
          disabled={cancelLoading || resetLoading || resetAndRunLoading}
          className="px-3 py-1.5 rounded-md border border-red-500/40 text-red-300 hover:bg-red-500/10 disabled:opacity-50 text-[11px]"
          data-testid="cancel-assessment-button"
        >
          {cancelLoading ? 'Stopping…' : 'Stop only'}
        </button>
      )}
    </div>
    {adminStatus.control_artifacts && adminStatus.control_artifacts.some((a) => a.exists) && (
      <details className="text-gray-500 font-sans text-[11px]">
        <summary className="cursor-pointer hover:text-gray-300">
          Control files ({adminStatus.control_artifacts.filter((a) => a.exists).length} active)
        </summary>
        <ul className="mt-1 space-y-0.5">
          {adminStatus.control_artifacts.filter((a) => a.exists).map((artifact) => (
            <li key={artifact.name} className="break-all">
              {artifact.name} · {artifact.size_bytes}b
              {artifact.modified_at ? ` · ${new Date(artifact.modified_at).toLocaleString()}` : ''}
            </li>
          ))}
        </ul>
      </details>
    )}
    {adminPollMeta && (
      <div className="text-gray-500">
        Poll #{adminPollMeta.count}
        {adminPollMeta.lastAt ? ` · last check ${adminPollMeta.lastAt}` : ''}
      </div>
    )}
    {adminPollMeta?.lastError && (
      <div className="text-red-400 font-sans">Poll error: {adminPollMeta.lastError}</div>
    )}
    {adminStatus.recent_events && adminStatus.recent_events.length > 0 && (
      <details className="text-gray-400">
        <summary className="cursor-pointer text-gray-500 font-sans hover:text-gray-300">
          Server error.log events ({adminStatus.recent_events.length})
        </summary>
        <ul className="mt-2 space-y-1 max-h-32 overflow-y-auto list-disc pl-4 font-sans text-[11px]">
          {adminStatus.recent_events.map((line, index) => (
            <li key={`${index}-${line.slice(0, 40)}`} className="break-all text-gray-400">
              {line}
            </li>
          ))}
        </ul>
      </details>
    )}
    {adminStatus.trace_tail && adminStatus.trace_tail.length > 0 && (
      <details className="text-gray-400" open={adminStatus.is_stale}>
        <summary className="cursor-pointer text-gray-500 font-sans hover:text-gray-300">
          Assessment trace ({adminStatus.trace_line_count ?? adminStatus.trace_tail.length} lines, showing last {adminStatus.trace_tail.length})
        </summary>
        <ul className="mt-2 space-y-1 max-h-64 overflow-y-auto list-none font-sans text-[11px]">
          {adminStatus.trace_tail.map((line, index) => (
            <li key={`trace-${index}-${line.slice(0, 40)}`} className="break-all text-gray-400">
              {line}
            </li>
          ))}
        </ul>
      </details>
    )}
    {adminStatus.trace_log && (
      <div className="text-gray-600 break-all">Trace file: {adminStatus.trace_log}</div>
    )}
  </div>
);

const formatLevel = (level?: string | null) =>
  (level || 'COMMON').replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());

const ScoreBar = ({ score, max, label }: { score: number; max: number; label: string }) => {
  const percentage = Math.min(100, Math.max(0, (score / max) * 100));
  return (
    <div className="mb-2">
      <div className="flex justify-between text-xs mb-1">
        <span className="text-gray-300">{label}</span>
        <span className="text-gray-400">{score}/{max}</span>
      </div>
      <div className="w-full bg-white/[0.06] rounded-full h-1.5">
        <div
          className="bg-gradient-to-r from-orange-500 to-orange-800 h-1.5 rounded-full transition-all duration-300"
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
};

const CollectibilityPanel: React.FC<CollectibilityPanelProps> = ({
  data,
  compact = false,
  defaultExpanded = false,
  showAdminActions = false,
  onRerunAssessment,
  onCancelAssessment,
  onResetPipeline,
  onResetAndRun,
  rerunLoading = false,
  cancelLoading = false,
  resetLoading = false,
  resetAndRunLoading = false,
  adminStatus = null,
  adminPollMeta,
}) => {
  const [showScoring, setShowScoring] = useState(false);
  const [showBreakdown, setShowBreakdown] = useState(false);
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  const status = data.assessment_status ?? null;
  const stars = data.base_rarity_stars ?? 0;
  const points = data.base_collector_points ?? 0;
  const isPending = status === 'pending';
  const isError = status === 'error';
  const isComplete =
    status === 'complete' || (!status && stars > 0);

  if (isPending) {
    if (compact) {
      return (
        <span className="inline-flex items-center gap-2 text-orange-300/90 text-sm">
          <span className="animate-spin rounded-full h-3 w-3 border-b-2 border-orange-500" aria-hidden />
          Scoring…
        </span>
      );
    }

    return (
      <div className="card p-6 flex flex-col items-center text-center min-h-[120px] justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500 mb-3" aria-hidden />
        <p className="text-orange-300 font-medium">Collectibility assessment in progress…</p>
        <p className="text-gray-400 text-sm mt-1">This usually takes under a minute.</p>
        {showAdminActions && onRerunAssessment && (
          <button
            type="button"
            onClick={() => void onRerunAssessment()}
            disabled={rerunLoading}
            className="mt-4 text-xs text-gray-400 hover:text-gray-200 disabled:opacity-50"
          >
            {rerunLoading ? 'Scheduling…' : adminStatus?.blocking_inline_required ? 'Run assessment (blocking, ~1-2 min)' : 'Queue new assessment'}
          </button>
        )}
        {showAdminActions && adminStatus && (
          <AdminAssessmentStatusPanel
            adminStatus={adminStatus}
            adminPollMeta={adminPollMeta}
            onCancelAssessment={onCancelAssessment}
            onResetPipeline={onResetPipeline}
            onResetAndRun={onResetAndRun}
            cancelLoading={cancelLoading}
            resetLoading={resetLoading}
            resetAndRunLoading={resetAndRunLoading}
          />
        )}
      </div>
    );
  }

  if (isError && stars <= 0) {
    return (
      <div className="card p-6 bg-red-900/15 border-red-700/30 text-center">
        <p className="text-red-300 font-medium">Collectibility assessment could not be completed</p>
        <p className="text-gray-400 text-sm mt-1">Try again in a moment or update the vehicle year/make/model.</p>
        {onRerunAssessment && (
          <button
            type="button"
            onClick={() => void onRerunAssessment()}
            disabled={rerunLoading}
            className="mt-4 px-4 py-2 bg-orange-600 hover:bg-orange-700 disabled:opacity-50 text-white text-sm rounded-lg"
          >
            {rerunLoading ? 'Scheduling…' : 'Retry assessment'}
          </button>
        )}
        {showAdminActions && adminStatus && (
          <AdminAssessmentStatusPanel
            adminStatus={adminStatus}
            adminPollMeta={adminPollMeta}
            onCancelAssessment={onCancelAssessment}
            onResetPipeline={onResetPipeline}
            onResetAndRun={onResetAndRun}
            cancelLoading={cancelLoading}
            resetLoading={resetLoading}
            resetAndRunLoading={resetAndRunLoading}
          />
        )}
      </div>
    );
  }

  if (!isComplete || stars <= 0) {
    return null;
  }

  const confidence = data.assessment_confidence || 'low';
  const isLowConfidence = confidence === 'low';

  if (compact) {
    return (
      <div
        className="inline-flex items-center gap-2 bg-orange-500/20 text-orange-200 text-sm font-semibold px-3 py-1.5 rounded-md border border-orange-500/30"
        title="Collectibility Index reflects the vehicle's heritage and rarity"
      >
        <span aria-label={`${stars} stars`}>{stars} <CtaIcon direction="star" compact /></span>
        <span>{points} pts</span>
        {isLowConfidence && (
          <span className="text-xs text-orange-300/80" title="Limited baseline match">~</span>
        )}
      </div>
    );
  }

  return (
    <div className="card p-6 border-orange-500/20 bg-orange-500/[0.04]">
      <button
        type="button"
        onClick={() => setIsExpanded((open) => !open)}
        aria-expanded={isExpanded}
        aria-controls="collectibility-panel-details"
        className="w-full flex flex-wrap items-start justify-between gap-4 text-left group"
      >
        <div className="min-w-0 flex-1">
          <h3 className="text-lg font-semibold text-orange-200 mb-1 flex items-center gap-2">
            Collectibility Index
            {data.assessment_version === '2.0' && (
              <span className="text-xs font-normal text-orange-400/80 bg-orange-900/30 px-2 py-0.5 rounded-md">v2</span>
            )}
          </h3>
          <p className="text-sm text-gray-400">
            {isExpanded
              ? 'Heritage score based on production rarity, significance, and market context'
              : `${formatLevel(data.base_rarity_level)} · ${points} collectibility pts`}
          </p>
        </div>
        <div className="flex items-start gap-3 shrink-0">
          <div className="text-right">
            <div className="text-2xl text-orange-300 tracking-wider">
              <span aria-label={`${stars} of 5 stars`}>{Array.from({ length: 5 }, (_, i) => <CtaIcon key={i} direction="star" className={i < stars ? "fill-current" : ""} />)}</span>
            </div>
            <div className="text-orange-100 font-bold text-xl">{points} pts</div>
            {isExpanded && (
              <div className="text-orange-300/80 text-sm">{formatLevel(data.base_rarity_level)}</div>
            )}
          </div>
          <span
            className="mt-1 text-orange-400/80 group-hover:text-orange-300 transition-colors"
            aria-hidden
          >
            <svg
              className={`w-5 h-5 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </span>
        </div>
      </button>

      {isExpanded && (
        <div id="collectibility-panel-details" className="mt-4 pt-4 border-t border-orange-700/25">
      {data.production_numbers && (
        <p className="text-sm text-gray-300 mb-3">
          <span className="text-gray-500">Production: </span>
          {data.production_numbers}
        </p>
      )}

      {data.vehicle_overview && (
        <p className="text-sm text-gray-300 leading-relaxed mb-4">{data.vehicle_overview}</p>
      )}

      {isLowConfidence && (
        <div className="text-xs text-orange-300/90 bg-orange-900/30 rounded-lg px-3 py-2 mb-3 border border-orange-700/40">
          Estimate: no exact baseline match for this make/model/year. Score may be revised as our catalog grows.
        </div>
      )}

      {data.rarity_breakdown && (
        <>
          <button
            type="button"
            onClick={() => setShowBreakdown(!showBreakdown)}
            className="text-sm text-orange-400 hover:text-orange-300 transition-colors mr-4"
          >
            {showBreakdown ? 'Hide score breakdown' : 'Score breakdown'} <CtaIcon direction={showBreakdown ? "up" : "down"} compact />
          </button>
          {showBreakdown && (
            <div className="mt-3 pt-3 border-t border-orange-700/30 space-y-1">
              <ScoreBar score={data.rarity_breakdown.production_rarity_score || 0} max={400} label="Production rarity" />
              <ScoreBar score={data.rarity_breakdown.historical_significance_score || 0} max={250} label="Historical significance" />
              <ScoreBar score={data.rarity_breakdown.desireability_score || 0} max={200} label="Desirability" />
              <ScoreBar score={data.rarity_breakdown.performance_heritage_score || 0} max={150} label="Performance heritage" />
              <div className="pt-2 border-t border-white/[0.08]">
                <ScoreBar score={data.rarity_breakdown.total_score || 0} max={1000} label="Total score" />
              </div>
            </div>
          )}
        </>
      )}

      <button
        type="button"
        onClick={() => setShowScoring(!showScoring)}
        className="text-sm text-orange-400 hover:text-orange-300 transition-colors mt-3 block"
      >
        {showScoring ? 'Hide' : 'How we scored this'} <CtaIcon direction={showScoring ? "up" : "down"} compact />
      </button>

      {showScoring && (
        <div className="mt-3 pt-3 border-t border-orange-700/30 text-sm text-gray-400 space-y-2">
          <p>
            <span className="text-gray-500">Method: </span>
            {data.assessment_version === '2.0'
              ? 'Baseline catalog match + bounded AI narrative (v2)'
              : 'AI-assisted heritage assessment'}
          </p>
          <p>
            <span className="text-gray-500">Confidence: </span>
            {confidence}
          </p>
          {data.key_factors?.positive_factors && data.key_factors.positive_factors.length > 0 && (
            <div>
              <span className="text-gray-500">Highlights: </span>
              {data.key_factors.positive_factors.slice(0, 3).join(' · ')}
            </div>
          )}
          <p className="text-xs">
            Build score (mod count) is separate from Collectibility. More logged mods raise your build score (up to 5).
            Builds with 15+ mods may have collectibility re-scored with a modified-vehicle cap.
          </p>
          {data.assessment_date && (
            <p className="text-xs text-gray-500">
              Assessed {new Date(data.assessment_date).toLocaleDateString()}
            </p>
          )}
        </div>
      )}

      {showAdminActions && onRerunAssessment && (
        <button
          type="button"
          onClick={() => void onRerunAssessment()}
          disabled={rerunLoading}
          className="mt-4 text-xs text-orange-400 hover:text-orange-300 disabled:opacity-50"
        >
          {rerunLoading ? 'Scheduling…' : 'Re-run collectibility assessment'}
        </button>
      )}
        </div>
      )}
    </div>
  );
};

export default CollectibilityPanel;
